<template>
  <div :class="bemBlock">
    <BaseFieldText
      ref="searchField"
      v-model="value"
      autocomplete="off"
      bem="the-search-field"
      input-type="search"
      name="the-search-field"
      label="Search here"
      :placeholder="$msg('search.input.placeholder', 'search')"
      @focus="focus"
      @focusout="delayedFocusout(focusout)"
      @keyup.down="focusSuggestion"
      @keyup.enter="quickSearch()"
    />
    <BaseImageSearch
      v-if="(searchAIEnabled && imageSearchEnabled) || isTestParticipant"
      :class="bemElement('image-search')"
    />
    <button
      v-if="(searchAIEnabled || isTestParticipant) && value"
      :class="['btn', bemElement('btn-reset')]"
      @click="reset"
    >
      {{ $msg('search.input.reset', 'search') }}
    </button>
    <button
      v-if="!searchAIEnabled && !isTestParticipant"
      :class="[bemElement('btn-close'), 'btn-close']"
      @click="hide"
      @focus="() => (isCloseBtnFocused = true)"
      @focusout="delayedFocusout(() => (isCloseBtnFocused = false))"
    >
      <span class="visually-hidden">{{
        $msg('search.input.reset', 'search')
      }}</span>
    </button>
    <p
      v-show="
        !searchAIEnabled &&
        !isLoadingSearch &&
        value !== '' &&
        productsCount === 0 &&
        ((quickSearchCommitted && !isFocusWithin) ||
          (searchCommitted && !isFocusWithin))
      "
      :class="bemElement('hero-message')"
      @click="focus"
    >
      {{ $msg('search.heromessage.noresultsfor', 'search', [value]) }}
    </p>
    <p v-show="message !== ''" :class="bemElement('message')">
      {{ message }}
    </p>
    <div
      v-if="shouldShowSuggestions"
      :class="[
        bemElement('suggestions-wrapper', {
          loading: quickSearchWillBeCommitted && hasQueryMinLength(),
        }),
      ]"
    >
      <ul :class="[bemElement('suggestions'), 'u-scrollbar-none']">
        <TheSearchSuggestion
          v-for="(res, index) in suggestions"
          :ref="`suggestions`"
          :key="index"
          :match="value"
          :text="res"
          @click.native.prevent="
            () => {
              suggestionClickHandler(res);
            }
          "
          @focus="() => (isSuggestionFocused = true)"
          @focusout="delayedFocusout(() => (isSuggestionFocused = false))"
          @keyup.native.enter="
            () => {
              suggestionClickHandler(res);
            }
          "
          @keyup.native.up="() => focusSuggestion(index - 1)"
          @keyup.native.left="() => focusSuggestion(index - 1)"
          @keyup.native.down="() => focusSuggestion(index + 1)"
          @keyup.native.right="() => focusSuggestion(index + 1)"
        />
      </ul>
      <div class="spinner-wrapper">
        <BaseSpinner inline tiny />
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import debounce from 'lodash/debounce';
import BaseFieldText from './forms/BaseFieldText.vue';
import BaseImageSearch from './BaseImageSearch.vue';
import BaseSpinner from './BaseSpinner.vue';
import TheSearchSuggestion from './TheSearchSuggestion.vue';
import { types } from '../store';
import bemClassesMixin from '../mixins/bemClassesMixin';
import EventBusConfig from '../config/eventBusConfig';
import getFrontConfig from '../../common/getFrontConfig';

const {
  searchAI: {
    isEnabledInFront,
    isImageSearchEnabled,
    debouncedSearchMilliseconds = 3000,
  },
} = getFrontConfig();

const theSearchValueMinLengthToBeReactive = 3;

const focusoutDelay = 40;

export default {
  name: 'TheSearchForm',

  components: {
    BaseFieldText,
    BaseImageSearch,
    BaseSpinner,
    TheSearchSuggestion,
  },

  mixins: [bemClassesMixin],

  props: {
    autocompleteSource: {
      type: String,
      default: '/suggest',
    },
    results: {
      type: Object,
      default: () => ({
        products: [],
        suggestedTerms: [],
      }),
    },
    searchSource: {
      type: String,
      default: '/search',
    },
  },

  data() {
    return {
      isCloseBtnFocused: false,
      isInputFocused: false,
      isSuggestionFocused: false,
      anySearchCommitted: false,
      quickSearchCommitted: false,
      quickSearchWillBeCommitted: false,
      searchCommitted: false,
      searchWillBeCommitted: false,
      value: this.defaults?.value ?? '',
      searchAIEnabled: isEnabledInFront,
      imageSearchEnabled: isImageSearchEnabled,
      debouncedQuickSearchMs: debouncedSearchMilliseconds,
    };
  },

  computed: {
    ...mapState({
      isLoadingSearch: (state) => state.search.isLoading,
      isImageSearch: (state) => !!state.search.base64Image,
      query: (state) => state.search.query,
    }),
    isFocusWithin() {
      return (
        this.isCloseBtnFocused ||
        this.isInputFocused ||
        this.isSuggestionFocused
      );
    },
    message() {
      if (!this.hasQueryMinLength() || !this.anySearchCommitted) return '';

      if (this.productsCount > 0) {
        // Si hay una sugerencia mejor y no tiene el foco, mostrar "¿Quisiste decir?"
        if (this.hasSuggestedProducts() && !this.isFocusWithin) {
          return this.$msg('search.message.doyoumean', 'search', [
            this.suggestedMatch,
          ]);
        }
        if (
          !this.searchAIEnabled &&
          !this.isTestParticipant &&
          (this.searchCommitted || !this.isFocusWithin)
        ) {
          return this.$msg('search.message.results', 'search', [
            this.productsCount,
          ]);
        }
        return '';
      }
      if (this.searchAIEnabled && this.isLoadingSearch) {
        return '';
      }
      if (
        (this.isInputFocused && !this.isLoadingSearch) ||
        this.searchAIEnabled
      ) {
        return this.$msg('search.message.noresultsfor', 'search', [this.value]);
      }
      return '';
    },
    shouldShowSuggestions() {
      return (
        this.hasSuggestions() &&
        this.message === '' &&
        this.value !== '' &&
        this.isFocusWithin &&
        !this.searchAIEnabled &&
        !this.isTestParticipant &&
        !this.searchCommitted
      );
    },
    productsCount() {
      if (this.results.productSearch) {
        return this.results.productSearch.count;
      }
      return 0;
    },
    productSearch() {
      if (this.results.productSearch) {
        return this.results.productSearch;
      }
      return {};
    },
    suggestedMatch() {
      return this.results && this.results.match;
    },
    suggestions() {
      if (
        this.results &&
        this.results.suggestedTerms &&
        Array.isArray(this.results.suggestedTerms.terms)
      ) {
        return this.results.suggestedTerms.terms.filter(
          (item) => item !== this.value
        ); // Not show the same value as a suggestion
      }
      return [];
    },
    trackedSearchText() {
      return this.suggestedMatch ?? this.value;
    },
    isTestParticipant() {
      return window.FRONT_CONFIG?.searchAI?.isTestParticipant;
    },
  },

  watch: {
    value(value) {
      this.quickSearchCommitted = false;
      this.searchCommitted = false;
      this.anySearchCommitted = false;
      if (this.searchWillBeCommitted) {
        // Do not do a quick search, a suggestion has been selected or enter has
        // been pressed
        return;
      }
      if (this.$store) {
        if (this.hasQueryMinLength()) {
          this.$store.commit(types.search.updateValue, this.value);
          this.debouncedQuickSearch();
        } else if (value === '') {
          this.$store.commit(types.search.updateValue, '');
        }
      }
    },
    productSearch() {
      if (this.searchWillBeCommitted) {
        this.quickSearchCommitted = false;
        this.searchCommitted = true;
        this.searchWillBeCommitted = false;
      } else {
        this.quickSearchCommitted = true;
        this.searchCommitted = false;
        this.quickSearchWillBeCommitted = false;
      }
      this.anySearchCommitted = true;
      this.$emit('searched', {
        value: this.value,
        productSearch: this.productSearch,
      });
    },
    query(value) {
      if (this.value !== value && value === '') {
        this.value = value;
      }
    },
  },

  created() {
    this.debouncedQuickSearch = debounce(
      this.quickSearch,
      this.searchAIEnabled || this.isTestParticipant
        ? this.debouncedQuickSearchMs
        : 500
    );
    this.$eventBus.$on(EventBusConfig.commands.TheSearch.hide, this.reset);
    this.$eventBus.$on(EventBusConfig.commands.TheSearch.toggle, this.reset);
    this.$eventBus.$on(EventBusConfig.commands.TheSearch.search, this.search);
  },

  beforeDestroy() {
    this.$eventBus.$off(EventBusConfig.commands.TheSearch.hide, this.reset);
    this.$eventBus.$off(EventBusConfig.commands.TheSearch.toggle, this.reset);
    this.$eventBus.$off(EventBusConfig.commands.TheSearch.search, this.search);
  },

  methods: {
    delayedFocusout(handler) {
      setTimeout(handler, focusoutDelay);
    },

    focus() {
      this.$refs.searchField.focus();
      this.isInputFocused = true;
    },

    focusout() {
      this.isInputFocused = false;
    },

    focusSuggestion(index = 0) {
      if (!Number.isInteger(index)) {
        // Default value is not applied if it receives an event or other non-numeric
        // eslint-disable-next-line no-param-reassign
        index = 0;
      }
      const suggestionComponentArray = this.$refs.suggestions;

      // No suggestions
      if (!suggestionComponentArray || suggestionComponentArray.length === 0) {
        return;
      }

      // eslint-disable-next-line no-param-reassign
      if (Number.isNaN(index)) index = 0;

      // Back to search field
      if (index < 0) {
        this.focus();
        return;
      }

      // Circular navigation
      if (index >= suggestionComponentArray.length) {
        // eslint-disable-next-line no-param-reassign
        index = 0;
      }

      suggestionComponentArray[index].focus();
    },

    hide() {
      this.$eventBus.$emit(EventBusConfig.commands.TheSearch.hide, this);
    },

    hasQueryMinLength() {
      return this.value.length >= theSearchValueMinLengthToBeReactive;
    },

    /**
     * Check whether the results correspond to the query or to a match suggested
     * by SFRA
     * @returns {boolean}
     */
    hasSuggestedProducts() {
      // The comparison must be key insensitive
      // Bag/bag/BAG return the same results
      return (
        !this.searchAIEnabled &&
        !this.isTestParticipant &&
        this.value?.toLowerCase() !== this.suggestedMatch?.toLowerCase()
      );
    },

    hasSuggestions() {
      return this.suggestions.length > 0;
    },

    reset() {
      this.value = '';
    },

    quickSearch() {
      if (this.hasQueryMinLength()) {
        this.debouncedQuickSearch.cancel();
        this.quickSearchWillBeCommitted = true;
        this.$store.dispatch(types.search.search);
      }
    },

    /**
     * Performs a search with the current value or provided searchText.
     * Handles the search flow to prevent race conditions between quick search and direct search.
     *
     * Flow:
     * 1. If searchText is provided (click on suggestion), do a direct search
     * 2. If no searchText (pressing enter), do a debounced search
     *
     * @param {string|null} searchText - Optional search text to override current value
     */
    search(searchText = null) {
      // Click en sugerencia - búsqueda directa
      this.debouncedQuickSearch.cancel();
      this.$nextTick(() => {
        this.searchWillBeCommitted = true;
        if (searchText !== null) {
          this.value = searchText;
        }
        this.$store.commit(types.search.updateValue, this.value);
        this.$store.dispatch(types.search.search);
        this.$refs.searchField.blur();
      });
    },

    suggestionClickHandler(theSearchSuggestion) {
      this.search(theSearchSuggestion?.text ?? theSearchSuggestion);
    },

    toggle() {
      this.$eventBus.$emit(EventBusConfig.commands.TheSearch.toggle, this);
    },
  },
};
</script>
